import { ArrowLeftRight, Clock, MapPin, Plus, Search } from "lucide-react";
import { matchSorter } from "match-sorter";
import { useMemo, useState } from "react";
import {
  live,
  reconciliationPayeeId,
  startingBalancePayeeId,
  type Account,
  type AccountId,
  type PayeeId,
  type PayeeSuggestions,
} from "@budgeter/shared";
import { useSnapshot } from "../../data/StoreContext";
import { cn, Sheet } from "../../ui";
import { looksLikeTransfer } from "../register/registerModel";
import { formatDistance, recentPayees } from "./payeeLists";

export type PayeePick =
  | { kind: "payee"; payeeId: PayeeId }
  | { kind: "new"; name: string }
  | { kind: "transfer"; accountId: AccountId };

export interface PayeePickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The current payee, shown in bold. */
  value?: PayeeId | null;
  /** Location suggestions; without them the list starts with recent payees. */
  suggestions?: PayeeSuggestions | null;
  /** Offer "Transfer: <account>" for these accounts. */
  transferAccounts?: Account[];
  onPick: (pick: PayeePick) => void;
}

/** Created by the app itself; still searchable, but never worth suggesting. */
const builtInPayees = new Set<string>([startingBalancePayeeId, reconciliationPayeeId]);

const rowClass =
  "flex min-h-[50px] w-full items-center gap-3 border-b border-line-soft px-1 text-left text-[15.5px] text-ink";
const headingClass = "px-1 pt-3 pb-1 text-[12px] font-bold tracking-[.5px] text-muted uppercase";

/**
 * Phone payee picker (SPEC §11.3): payees within 1km nearest first, then recent ones; typing
 * searches every payee and offers to create a new one.
 */
export function PayeePicker({
  open,
  onOpenChange,
  value,
  suggestions,
  transferAccounts = [],
  onPick,
}: PayeePickerProps) {
  const s = useSnapshot();
  const [query, setQuery] = useState("");
  const payees = useMemo(() => live(s, "payees"), [s]);
  const recent = useMemo(
    () =>
      (suggestions?.recent ?? recentPayees(s.transactions.values(), payees)).filter(
        (id) => !builtInPayees.has(id),
      ),
    [suggestions, s.transactions, payees],
  );
  const q = query.trim();
  const matches = useMemo(() => (q ? matchSorter(payees, q, { keys: ["name"] }) : []), [payees, q]);
  const transferMatches = useMemo(() => {
    if (!q) {
      return transferAccounts;
    }
    // "Tra", "transfer" and "Transfer: Home" all find transfers, as well as the account name.
    if ("transfer".startsWith(q.toLowerCase())) {
      return transferAccounts;
    }
    const rest = q.replace(/^transfer\s*:?\s*/i, "");
    return rest ? matchSorter(transferAccounts, rest, { keys: ["name"] }) : transferAccounts;
  }, [transferAccounts, q]);
  const exact = payees.some((p) => p.name.trim().toLowerCase() === q.toLowerCase());
  const transferLike = useMemo(
    () =>
      looksLikeTransfer(
        q,
        live(s, "accounts")
          .filter((a) => !a.closed)
          .map((a) => a.name),
      ),
    [s, q],
  );

  function pick(p: PayeePick) {
    onPick(p);
    setQuery("");
    onOpenChange(false);
  }

  const payeeRow = (id: PayeeId, icon: "near" | "recent" | "none", hint?: string) => {
    const name = s.payees.get(id)?.name ?? "";
    return (
      <button
        key={`${icon}-${id}`}
        type="button"
        className={rowClass}
        onClick={() => pick({ kind: "payee", payeeId: id })}
        aria-current={id === value ? "true" : undefined}
      >
        {icon === "near" && <MapPin size={16} className="shrink-0 text-accent" aria-hidden />}
        {icon === "recent" && <Clock size={16} className="shrink-0 text-faint" aria-hidden />}
        <span className={cn("min-w-0 flex-1 truncate", id === value && "font-extrabold")}>
          {name}
        </span>
        {hint && <span className="text-[13px] text-muted">{hint}</span>}
      </button>
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange} title="Payee">
      <label className="sticky top-0 z-10 mt-1 flex h-[42px] items-center gap-2 rounded-[10px] bg-bg px-3">
        <Search size={16} className="text-muted" aria-hidden />
        <span className="sr-only">Search payees</span>
        <input
          type="search"
          placeholder="Search all payees"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="min-w-0 flex-1 border-0 bg-transparent text-[16px] text-ink outline-none"
        />
      </label>
      {q ? (
        <div>
          {!exact && !transferLike && (
            <button
              type="button"
              className={cn(rowClass, "font-bold text-accent-strong")}
              onClick={() => pick({ kind: "new", name: q })}
            >
              <Plus size={16} className="shrink-0" aria-hidden />
              Create payee "{q}"
            </button>
          )}
          {matches.map((p) => payeeRow(p.id, "none"))}
          {transferMatches.map((a) => (
            <button
              key={a.id}
              type="button"
              className={rowClass}
              onClick={() => pick({ kind: "transfer", accountId: a.id })}
            >
              <ArrowLeftRight size={16} className="shrink-0 text-faint" aria-hidden />
              <span className="min-w-0 flex-1 truncate">Transfer: {a.name}</span>
            </button>
          ))}
        </div>
      ) : (
        <div>
          {suggestions && suggestions.nearby.length > 0 && (
            <>
              <h3 className={headingClass}>Nearby · within 1 km</h3>
              {suggestions.nearby.map((n) =>
                payeeRow(n.payeeId, "near", formatDistance(n.distanceMeters)),
              )}
            </>
          )}
          {recent.length > 0 && (
            <>
              <h3 className={headingClass}>Recent</h3>
              {recent.map((id) => payeeRow(id, "recent"))}
            </>
          )}
          {transferAccounts.length > 0 && (
            <>
              <h3 className={headingClass}>Transfer</h3>
              {transferAccounts.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  className={rowClass}
                  onClick={() => pick({ kind: "transfer", accountId: a.id })}
                >
                  <ArrowLeftRight size={16} className="shrink-0 text-faint" aria-hidden />
                  <span className="min-w-0 flex-1 truncate">Transfer: {a.name}</span>
                </button>
              ))}
            </>
          )}
          {payees.length === 0 && (
            <p className="py-4 text-[14px] text-muted">Type a name to create your first payee.</p>
          )}
        </div>
      )}
    </Sheet>
  );
}
